import type * as React from "react";

import { Badge as CrowBadge, type BadgeTone } from "@crow/ui/badge";
import { cn } from "../../lib/utils";

/**
 * One label for every kind and state (brand/crow-charte.html): the design system's Badge
 * (`design-system/ui`, copied in `src/crow`) under Crowdex's names. `default` is neutral,
 * `outline` a thin bordered label; the state tones follow the charter: `attention` (the amber
 * companion) is only what waits on the user, `running` is cobalt, `success` green, `destructive`
 * red. `mono` is for branches, paths and commands only.
 */
const TONE = {
	default: "neutral",
	outline: "neutral",
	attention: "info",
	running: "accent",
	success: "success",
	destructive: "danger",
} as const satisfies Record<string, BadgeTone>;

/** `md` is the charter's 22 px label; `sm` a 20 px one, for dense rows. */
const SIZE = {
	sm: "h-5",
	md: "",
} as const;

export type BadgeVariant = keyof typeof TONE;

function Badge({
	className,
	mono = false,
	size = "md",
	variant = "default",
	children,
	...props
}: React.ComponentProps<"span"> & {
	variant?: BadgeVariant | null;
	size?: keyof typeof SIZE | null;
	mono?: boolean | null;
}) {
	const kind = variant ?? "default";
	return (
		<CrowBadge
			data-slot="badge"
			data-variant={kind}
			tone={TONE[kind]}
			className={cn(
				"w-fit shrink-0 gap-1 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3",
				SIZE[size ?? "md"],
				kind === "outline" && "border border-hairline-strong bg-transparent text-muted-foreground",
				mono && "font-mono font-normal",
				className,
			)}
			{...props}
		>
			{children}
		</CrowBadge>
	);
}

export { Badge };
